import clsx from "clsx";
import { MdAccountBalance } from "react-icons/md";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import { formatCurrency, getCurrencyDataByCode } from "../../helpers/utils";
import React, { memo, useCallback, useId, useMemo } from "react";
import {
  COUNT_UP_ANIMATION_DURATION,
  ICON_SIZES,
} from "../../helpers/constants";
import { Tooltip } from "react-tooltip";
import useMobile from "src/hooks/useMobile";
import CountUp from "react-countup";

interface IBankCard {
  account: string;
  bank?: string;
  owner?: string;
  number?: string;
  name?: string;
  selected?: boolean;
  actions?: Array<any>;
  variant?: "blue" | "gray" | "custom";
  actionsTrigger?: "hover" | "normal";
  customStyles?: any;
  className?: string;
  currency: string;
  available?: any;
  onTitleClick?: () => void;
}

const BankCard: React.FC<IBankCard> = ({
  bank,
  owner,
  number,
  name,
  selected = false,
  actions = [],
  account,
  variant = "blue",
  actionsTrigger = "",
  customStyles = {},
  className,
  currency,
  available = null,
  onTitleClick,
}) => {
  const tooltipId = useId();
  const { isMobile } = useMobile();

  const hasDecimals = useCallback((value: any) => {
    return value % 1 !== 0;
  }, []);

  const getVariantClasses = () => {
    switch (variant) {
      case "gray": {
        return {
          "bg-gray-100": !selected,
          "text-gray-900": !selected,
          "bg-expenser-gray/80 text-white": selected,
          "hover:bg-expenser-gray/80 hover:text-gray-900": !selected,
          "hover:bg-expenser-gray hover:text-white": selected,
        };
      }
      case "blue": {
        return {
          "bg-secondary-100": !selected,
          "text-secondary-900": !selected,
          "bg-primary/80 text-white": selected,
          "hover:bg-primary/80 hover:text-white": !selected,
          "hover:bg-primary hover:text-white": selected,
        };
      }

      case "custom": {
        return {};
      }
    }
  };

  const SHARED_STYLES = {
    relative: true,
    flex: true,
    "flex-col": true,
    "rounded-md md:rounded-md": true,
    "text-sm": true,
    "p-3": true,
    "gap-5": true,
    "transition-colors": true,
    group: true,
    "shadow-md": true,
  };

  const currencyData = useMemo(() => {
    return getCurrencyDataByCode({ code: currency });
  }, [currency]);

  return (
    <>
      <div
        className={clsx([SHARED_STYLES, getVariantClasses(), className])}
        style={customStyles}
      >
        <div
          className={clsx([
            "text-right",
            "flex",
            "flex-row",
            "gap-4",
            "justify-between",
            "items-center",
          ])}
        >
          <MdAccountBalance size={ICON_SIZES.LG} />
          <div className={clsx(["flex", "flex-col"])}>
            <p
              className={clsx([
                { italic: !bank, "text-sm": bank, "text-xs": !bank },
              ])}
            >
              {bank || "Banco no especificado"}
            </p>
            <p
              className={clsx([
                {
                  italic: !currencyData?.name,
                  "text-sm": currencyData?.name,
                  "text-xs": !currencyData?.name,
                },
              ])}
            >
              {currencyData?.name || "Moneda no especificada"}
            </p>
          </div>
        </div>

        <div className={clsx(["pr-0", "xl:pr-8"])}>
          <p className={clsx(["truncate", "text-sm", "md:text-base"])}>
            <span
              data-tooltip-id={tooltipId}
              data-tooltip-content={isMobile ? "" : "Editar"}
              data-tooltip-position-strategy="fixed"
              data-tooltip-offset={0}
              className={clsx([
                "inline-block",
                "relative",
                "font-medium",
                {
                  "after-animated-underline after:bg-current": onTitleClick,
                },
              ])}
              onClick={() => {
                onTitleClick?.();
              }}
            >
              {name || "..."}
            </span>
            {onTitleClick && (
              <Tooltip
                id={tooltipId}
                style={{ zIndex: 99 }}
                positionStrategy="fixed"
              />
            )}
          </p>
          {available !== null && (
            <div
              className={clsx([
                "text-base",
                "md:text-lg",
                "font-medium",
                "flex",
                "flex-row",
                "gap-1",
                "items-center",
                "w-full",
                "justify-start",
                "pointer-events-none",
              ])}
            >
              <span>
                <CountUp
                  end={available}
                  duration={COUNT_UP_ANIMATION_DURATION}
                  separator=","
                  decimals={hasDecimals(available) ? 2 : 0}
                  delay={0.1}
                  preserveValue
                  prefix={currencyData?.symbol || "?"}
                />
                {/* {`${formatCurrency({
                  value: available,
                  currency: currencyData?.symbol || "?",
                })}`} */}
              </span>
            </div>
          )}
          {number && available === null && (
            <p className={clsx(["font-medium", "truncate", "text-base"])}>
              {number || <span className="text-xs pr-4"></span>}
            </p>
          )}
        </div>
        {actions?.length > 0 && (
          <div
            className={clsx([
              "absolute",
              "bottom-0",
              "right-0",
              "p-2",
              {
                "md:hidden md:group-hover:flex": actionsTrigger === "hover",
              },
            ])}
            onClick={(e: any) => {
              e?.stopPropagation?.();
            }}
          >
            <ButtonGroup gap={1} buttons={actions} />
          </div>
        )}
      </div>
    </>
  );
};

export default memo(BankCard);
